import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LuckyWheel, type LuckyWheelHandle } from './LuckyWheel';
import { isLiveTestWheel, isServerBackedWheel, requestWheelSpin } from './luckyWheelApi';
import { WheelAudio } from './WheelAudio';
import type { SpinResult, WheelPhase, WheelStatus } from './wheelTypes';
import './LuckyWheel.css';

const BONUS_REDIRECT_AMOUNTS = new Set([5, 10, 20, 50, 100, 200, 500]);

function formatAznMinor(minor: number): string {
  const safe = Number.isFinite(minor) ? Math.trunc(minor) : 0;
  const major = Math.trunc(safe / 100);
  const cents = Math.abs(safe % 100);
  return `${major}.${String(cents).padStart(2, '0')} AZN`;
}

function formatExpiry(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function TestBonusCard({ status }: { status: WheelStatus | SpinResult }) {
  const { t } = useTranslation('wheel');
  return (
    <div className="lw-test-card" aria-label={t('testLedger')}>
      <strong>{t('testLedger')}</strong>
      <dl>
        <div>
          <dt>{t('testBalance')}</dt>
          <dd>{formatAznMinor(Number(status.testBalanceMinor || 0))}</dd>
        </div>
      </dl>
      <p>{t('testNotWithdrawable')}</p>
      <p>{t('testExpires', { when: formatExpiry(status.testExpiresAt) })}</p>
    </div>
  );
}

interface LuckyWheelModalProps {
  open: boolean;
  status: WheelStatus;
  onClose: () => void;
  onSpun: (result: SpinResult) => void;
}

function ConfettiBurst({ active }: { active: boolean }) {
  const bits = useMemo(
    () =>
      Array.from({ length: 64 }, (_, i) => {
        const angle = (Math.PI * 2 * i) / 64 + i * 0.04;
        const dist = 110 + (i % 9) * 28;
        return {
          id: i,
          color: ['#ff4d00', '#ff2d6a', '#ffd000', '#00d4ff', '#7cff2e', '#ff1744', '#fff4c4'][i % 7],
          dx: `${Math.cos(angle) * dist}px`,
          dy: `${Math.sin(angle) * dist - 48}px`,
          delay: `${(i % 12) * 18}ms`,
        };
      }),
    [],
  );
  if (!active) return null;
  return (
    <div className="lw-confetti" aria-hidden="true">
      {bits.map((bit) => (
        <span
          key={bit.id}
          className="lw-bit"
          style={
            {
              background: bit.color,
              '--dx': bit.dx,
              '--dy': bit.dy,
              animationDelay: bit.delay,
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}

export function LuckyWheelModal({ open, status, onClose, onSpun }: LuckyWheelModalProps) {
  const { t } = useTranslation('wheel');
  const navigate = useNavigate();
  const wheelRef = useRef<LuckyWheelHandle>(null);
  const spinningRef = useRef(false);
  const aliveRef = useRef(false);
  const [audio, setAudio] = useState<WheelAudio | null>(null);
  const [phase, setPhase] = useState<WheelPhase>('idle');
  const [result, setResult] = useState<SpinResult | null>(null);
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    aliveRef.current = true;
    const nextAudio = new WheelAudio();
    setAudio(nextAudio);
    setMuted(nextAudio.muted);
    setLeaving(false);
    void nextAudio.unlock().then(() => nextAudio.open());
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('lw-open');
    return () => {
      aliveRef.current = false;
      document.body.style.overflow = prev;
      document.body.classList.remove('lw-open');
      spinningRef.current = false;
      wheelRef.current?.stop();
      nextAudio.dispose();
      setAudio(null);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setPhase('idle');
      setResult(null);
      setError(null);
      spinningRef.current = false;
    }
  }, [open]);

  const close = useCallback(() => {
    if (spinningRef.current || leaving) return;
    setLeaving(true);
    audio?.closeFx();
    window.setTimeout(() => onClose(), 320);
  }, [audio, leaving, onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  const toggleMute = async () => {
    if (!audio) return;
    await audio.unlock();
    audio.setMuted(!audio.muted);
    setMuted(audio.muted);
  };

  const spin = async () => {
    if (spinningRef.current || phase === 'spinning') return;
    if (status.requiresLogin) {
      setError(t('loginRequired'));
      return;
    }
    if (!status.available && !status.demo) return;
    spinningRef.current = true;
    setPhase('spinning');
    setResult(null);
    setError(null);
    try {
      await audio?.unlock();
      audio?.click();
      const next = await requestWheelSpin(isServerBackedWheel(status));
      await wheelRef.current?.spinTo(next.segmentIndex);
      if (!aliveRef.current) return;
      setResult(next);
      setPhase('result');
      audio?.win();
      onSpun(next);
      const shouldRedirect =
        !next.demo &&
        !isLiveTestWheel(next) &&
        next.credited &&
        BONUS_REDIRECT_AMOUNTS.has(Number(next.amountAzn));
      if (shouldRedirect) {
        window.setTimeout(() => {
          if (!aliveRef.current) return;
          onClose();
          navigate('/bonus-games');
        }, 1600);
      }
    } catch (err) {
      if (!aliveRef.current) return;
      if (err instanceof Error && err.message === 'spin_cancelled') return;
      setError(t('spinFailed'));
      setPhase('idle');
      audio?.stop();
    } finally {
      spinningRef.current = false;
    }
  };

  if (!open) return null;

  const busy = phase === 'spinning';
  const liveTest = isLiveTestWheel(status);
  const needsLogin = Boolean(status.requiresLogin);
  const dailyConsumed = !status.demo && !status.available && !needsLogin;
  const usedLive = dailyConsumed && phase !== 'result';
  const won = Boolean(result);

  return createPortal(
    <div className={`lw-overlay${leaving ? ' is-leaving' : ''}`} role="dialog" aria-modal="true" aria-labelledby="lw-title">
      <button type="button" className="lw-overlay-dismiss" onClick={close} disabled={busy} aria-label={t('close')} />
      <div className="lw-sheet">
        <div className="lw-stars" aria-hidden="true">
          <span className="lw-star s1" />
          <span className="lw-star s2" />
          <span className="lw-star s3" />
          <span className="lw-star s4" />
          <span className="lw-star s5" />
          <span className="lw-star s6" />
        </div>
        <div className="lw-sheet-bar">
          <button
            type="button"
            className="lw-icon-btn"
            onClick={() => void toggleMute()}
            aria-label={muted ? t('unmute') : t('mute')}
            title={muted ? t('unmute') : t('mute')}
          >
            {muted ? (
              <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
                <path
                  d="M4 10 H8 L13 6 V18 L8 14 H4 Z M16.5 9.5 L20.5 14.5 M20.5 9.5 L16.5 14.5"
                  fill="currentColor"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
                <path d="M4 10 H8 L13 6 V18 L8 14 H4 Z" fill="currentColor" />
                <path
                  d="M16 9.5 C17.4 10.6 17.4 13.4 16 14.5 M18.3 7.8 C20.8 9.7 20.8 14.3 18.3 16.2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </button>
          <div className="lw-modal-brand">
            <small>VBB PLAY</small>
          </div>
          <button type="button" className="lw-icon-btn lw-close-btn" onClick={close} disabled={busy} aria-label={t('close')}>
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
              <path d="M6 6 L18 18 M18 6 L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" fill="none" />
            </svg>
          </button>
        </div>
        <h2 id="lw-title" className="lw-hero-title">{t('title')}</h2>
        <p className="lw-sheet-copy">{t('description')}</p>
        {liveTest ? <span className="lw-test-ribbon">{t('testBadge')}</span> : null}
        {status.requiresLogin ? <span className="lw-demo-ribbon">{t('loginRequiredBadge')}</span> : null}
        {status.demo && !status.requiresLogin ? <span className="lw-demo-ribbon">{t('demoBadge')}</span> : null}
        {usedLive ? (
          <p className="lw-used-banner" role="status">
            {t('used')}
          </p>
        ) : null}

        <div className="lw-sheet-stage">
          <LuckyWheel
            ref={wheelRef}
            variant="full"
            audio={audio}
            celebrating={phase === 'result' && won}
            hubLabel={t('hubSpin')}
            hubBusy={busy}
            hubDisabled={busy || usedLive || needsLogin}
            onHubClick={() => void spin()}
          />
          <ConfettiBurst active={phase === 'result' && won} />
        </div>

        {liveTest ? <TestBonusCard status={result || status} /> : null}

        {error ? <p className="lw-status lw-sheet-error">{error}</p> : null}

        {result ? (
          <div className="lw-result-overlay">
            <div className="lw-result win">
              <span className="lw-result-kicker">{t('winTitle')}</span>
              <strong className="lw-prize-amount">{result.amountAzn} AZN</strong>
              <p>{t('winAmount', { amount: result.amountAzn })}</p>
              {liveTest ? <p>{t('testWinNote', { amount: result.amountAzn })}</p> : null}
              {liveTest ? <p>{t('testPrize', { amount: result.amountAzn })}</p> : null}
              {result.demo ? <p>{t('demoWinNote')}</p> : null}
              {dailyConsumed || !result.demo ? <p className="lw-used-note">{t('used')}</p> : null}
              <button type="button" className="lw-spin-btn" onClick={close}>
                {t('closeResult')}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
